import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { Controllers } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import TableC from "@/CommonElements/TableC";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import {
  useHousingComprehensiveDetailReport,
  useHousingComprehensiveDetailReportMutation,
} from "@/services/reports";
import { cn, filterObject } from "@/utils/helpers";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useSystem } from "@/utils/hooks/useLocation";
import { useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Container, Spinner } from "reactstrap";

export const HousingComprehensiveDetailReport = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { getLocalizedText } = useLocalizedText();

  const {
    mutate: generateHousingComprehensiveDetailReport,
    isPending: isGeneratingHousingComprehensiveDetailReport,
  } = useHousingComprehensiveDetailReportMutation();

  const form = useForm({
    defaultValues: {
      format: "pdf",
      orientation: "portrait",
    },
    disabled: isGeneratingHousingComprehensiveDetailReport,
  });

  const format = form.watch("format");
  const isJson = format === "json";

  const {
    control,
    formState: { errors },
    getValues,
  } = form;

  const filters = filterObject({
    ...form.getValues(),
    format: "json",
    page,
  });

  const {
    data: housingComprehensiveDetailRes,
    isLoading: isLoadingHousingComprehensiveDetail,
    refetch,
    isRefetching: isRefetchingHousingComprehensiveDetail,
  } = useHousingComprehensiveDetailReport({
    params: filters,
  });

  const rows = housingComprehensiveDetailRes?.data?.data || [];
  const meta = housingComprehensiveDetailRes?.data?.meta || {};

  const formText = {
    json: {
      buttonText: t("VIEW_REPORTS"),
    },
    pdf: {
      buttonText: t("EXPORT_PDF"),
    },
    excel: {
      buttonText: t("EXPORT_EXCEL"),
    },
  };

  const formContent = formText[format] || formText.pdf;

  useEffect(() => {
    if (isJson && page > 1) {
      refetch();
    }
  }, [page, isJson, refetch]);

  const ctrls = [
    {
      labelProps: { children: t("REPORT_FORMAT") },
      controllerProps: {
        name: "format",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="format"
            placeholder={t("REPORT_FORMAT")}
            status={error ? "error" : undefined}
            options={[
              { value: "json", label: t("VIEW_REPORTS") },
              { value: "pdf", label: t("EXPORT_PDF") },
              { value: "excel", label: t("EXPORT_EXCEL") },
            ]}
          />
        ),
      },
    },
    !isJson && {
      labelProps: { children: t("ORIENTATION") },
      controllerProps: {
        name: "orientation",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="orientation"
            placeholder={t("ORIENTATION")}
            status={error ? "error" : undefined}
            options={[
              { value: "portrait", label: t("PORTRAIT") },
              { value: "landscape", label: t("LANDSCAPE") },
            ]}
          />
        ),
      },
    },
    {
      labelProps: { children: t("BUILDING_NAME") },
      controllerProps: {
        name: "house_name",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            allowClear
            id="housing-comprehensive-detail-house_name"
            placeholder={t("BUILDING_NAME_PLACEHOLDER")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("BUILDING_NO") },
      controllerProps: {
        name: "building_no",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            allowClear
            id="housing-comprehensive-detail-building_no"
            placeholder={t("BUILDING_NO_PLACEHOLDER")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("BUILDING_CODE") },
      controllerProps: {
        name: "building_code",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            allowClear
            id="housing-comprehensive-detail-building_code"
            placeholder={t("BUILDING_CODE_PLACEHOLDER")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("SERVICE_CENTER") },
      controllerProps: {
        name: "service_center_id",
        render: ({ field, fieldState: { error } }) => (
          <SelectCachedOptions
            {...field}
            allowClear
            id={field.name}
            placeholder={t("SERVICE_CENTER")}
            status={error ? "error" : undefined}
            optionKey="service_centers"
            options={(serviceCenter) => ({
              value: serviceCenter.id,
              label: getLocalizedText(
                serviceCenter.name_ar,
                serviceCenter.name_en
              ),
            })}
          />
        ),
      },
    },
    {
      labelProps: { children: t("PERMIT_NO") },
      controllerProps: {
        name: "permit_no",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            allowClear
            id="housing-comprehensive-detail-permit_no"
            placeholder={t("PERMIT_NO_PLACEHOLDER")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
  ].filter(Boolean);

  const columns = useMemo(() => {
    const firstRow = rows[0];

    if (!firstRow || typeof firstRow !== "object") {
      return [];
    }

    return [
      {
        name: t("SERIAL"),
        key: "serial",
        filterable: false,
      },
      {
        name: t("CONTRACT_NUMBER"),
        key: "contract_id",
        filterable: false,
      },
      {
        name: t("CITY"),
        key: "city_id",
        filterable: false,
      },
      {
        name: t("HOUSING_COMPREHENSIVE_DETAIL_COL_CAMPAIGN_OR_GROUP_NUMBER"),
        key: "campaign_or_group_number",
        filterable: false,
      },
      {
        name: t("CAMPAIGN_OR_GROUP_NAME"),
        key: "campaign_or_group_name",
        filterable: false,
      },
      {
        name: t("ORGANIZER"),
        key: "organizer",
        filterable: false,
      },
      {
        name: t("COUNTRY"),
        key: "country",
        filterable: false,
      },
      {
        name: t("STATE"),
        key: "state",
        filterable: false,
      },
      {
        name: t("PILGRIMS_COUNT"),
        key: "pilgrims",
        filterable: false,
      },
      {
        name: t("HOUSING_COMPREHENSIVE_DETAIL_COL_ACTUAL_COUNT"),
        key: "actual_count",
        filterable: false,
      },
      {
        name: t("PERMIT_NO"),
        key: "permit_no",
        filterable: false,
      },
      {
        name: t("BUILDING_NO"),
        key: "building_no",
        filterable: false,
      },
      {
        name: t("BUILDING_CODE"),
        key: "building_code",
        filterable: false,
      },
      {
        name: t("BUILDING_NAME"),
        key: "building_name",
        filterable: false,
      },
      {
        name: t("ADDRESS"),
        key: "address",
        filterable: false,
      },
      {
        name: t("SERVICE_CENTER"),
        key: "service_center",
        filterable: false,
      },
    ].filter((column) => column.key in firstRow);
  }, [rows, t]);

  const onSubmit = (data) => {
    const payload = {
      ...getValues(),
      page,
    };

    if (data.format === "json") {
      setPage(1);
      return refetch();
    }

    generateHousingComprehensiveDetailReport({ params: payload });
  };

  const backTo =
    window.history.length > 1 ? -1 : `/app/madina/housing-reports`;

  const isLoading =
    isGeneratingHousingComprehensiveDetailReport ||
    isLoadingHousingComprehensiveDetail;

  return (
    <div>
      <PageHeader title={t("HOUSING_COMPREHENSIVE_DETAIL_TITLE")} sticky={false} />
      <Container fluid>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mb-4">
          <Controllers
            watch={form.watch}
            controllers={ctrls.map((config) => ({
              ...config,
              labelProps: {
                ...config.labelProps,
                className: cn("fw-bold", config.labelProps.className),
              },
              controllerProps: {
                ...config.controllerProps,
                control,
                errors,
              },
              layout: {
                colProps: { xs: 6, md: 6 },
              },
            }))}
          />
          <ActionsFooter>
            <Button
              disabled={isLoading}
              className="btn btn-primary btn-sm"
              type="submit"
            >
              {isLoading ? <Spinner size="sm" /> : formContent.buttonText}
            </Button>
            <Link
              to={backTo}
              disabled={isLoading}
              style={{ pointerEvents: isLoading ? "none" : "auto" }}
              className="btn btn-danger btn-sm"
              type="button"
            >
              {t("CANCEL")}
            </Link>
            <button
              onClick={() => form.reset()}
              disabled={isLoading}
              className="btn btn-outline-primary btn-sm"
              type="button"
            >
              {t("RESET")}
            </button>
          </ActionsFooter>
        </form>

        {isJson && (
          <TableC
            columnsFilter={false}
            data={rows}
            columns={columns}
            loading={
              isLoadingHousingComprehensiveDetail ||
              isRefetchingHousingComprehensiveDetail
            }
            skeletonCount={10}
            pagination={{
              current: page,
              total: meta?.total || 0,
              pageSize: meta?.per_page || 10,
              onChange: (newPage) => {
                setPage(newPage);
              },
            }}
          />
        )}
      </Container>
    </div>
  );
};
